import * as React from "react";
import { Mic, MicOff, PhoneOff, AlertCircle, Copy, Check, Ear, EarOff, LocateFixed, Volume2, VolumeX } from "lucide-react";
import { DysonSphere } from "@/components/ui/DysonSphere";
import { useVoiceCall } from "./VoiceCallContext";
import { useOrbPosition } from "./useOrbPosition";
import { useOrbFeedback } from "./feedback";
import type { VoiceMode } from "@/lib/api-types";
import { ThinkingPulse } from "./ThinkingPulse";
import { cn } from "@/lib/cn";

/** What each mode is called in the UI; the trade-off is in the title. */
const MODE_LABEL: Record<VoiceMode, { name: string; hint: string }> = {
  pipeline: { name: "Standard", hint: "Omni's full brain on every turn" },
  "nova-sonic": { name: "Expressive", hint: "Faster, more natural voice (Nova Sonic) — costs more" },
  "gemini-live": { name: "Gemini Live", hint: "Faster, more natural voice (Gemini on Google Cloud) — costs more" },
};

/** The one word under the orb: what it is doing right now. */
function orbLabel(connecting: boolean, voiceActive: boolean, agentState: string, micActive: boolean): string {
  if (connecting) return "Connecting";
  if (!voiceActive) return "";
  if (!micActive && agentState === "listening") return "Muted";
  if (agentState === "thinking") return "Thinking";
  if (agentState === "speaking") return "Speaking";
  return "Listening";
}

/**
 * Short-lived moments the orb reacts to: a spark when an answer finishes and a
 * squish when it is clicked. Both are keyed counters so the CSS animation
 * restarts on every moment instead of only the first.
 */
function useOrbMoments(speaking: boolean, active: boolean) {
  const [sparkKey, setSparkKey] = React.useState(0);
  const [squishKey, setSquishKey] = React.useState(0);
  const wasSpeaking = React.useRef(false);
  React.useEffect(() => {
    if (wasSpeaking.current && !speaking && active) setSparkKey((k) => k + 1);
    wasSpeaking.current = speaking;
  }, [speaking, active]);
  return { sparkKey, squishKey, squish: () => setSquishKey((k) => k + 1) };
}

const SPARKS = [
  { x: "-26px", y: "-30px" },
  { x: "30px", y: "-24px" },
  { x: "2px", y: "-40px" },
] as const;

export function FloatingVoiceOrb() {
  const {
    available,
    modes,
    mode,
    setMode,
    handsFree,
    wakeAllowed,
    setHandsFree,
    wakeState,
    wakePhrase,
    voiceActive,
    connecting,
    agentState,
    micVolume,
    interimText,
    assistantStreaming,
    assistantLevel,
    micActive,
    error,
    toggleVoice,
    toggleMic,
    cancelActiveCall,
  } = useVoiceCall();

  const [expanded, setExpanded] = React.useState(false);
  const [copiedError, setCopiedError] = React.useState(false);


  const handleCopyError = async () => {
    if (!error) return;
    try {
      await navigator.clipboard.writeText(error);
      setCopiedError(true);
      setTimeout(() => setCopiedError(false), 2000);
    } catch (err) {
      console.error("Failed to copy error message: ", err);
    }
  };

  // Derive DysonSphere animation state based on assistant status
  const getDysonMode = () => {
    if (connecting) return "listening";
    if (!voiceActive) return "idle";
    if (agentState === "listening") return "listening";
    if (agentState === "thinking") return "thinking";
    if (agentState === "speaking") return "speaking";
    return "idle";
  };

  // A starting call already looks active: the click is acknowledged at once
  // and the microphone is recording while the room connects.
  const isActive = voiceActive || connecting;
  const { sparkKey, squishKey, squish } = useOrbMoments(agentState === "speaking", isActive);
  const label = orbLabel(connecting, voiceActive, agentState, micActive);
  const orbPos = useOrbPosition();
  const feedback = useOrbFeedback(connecting, voiceActive);
  const { right: onRight, bottom: onBottom } = orbPos.placement;

  // The server has no voice configured (OMNI_LIVEKIT_URL) → no orb.
  if (!available) return null;

  return (
    <div
      className={cn(
        "fixed z-[100] flex flex-col gap-3 select-none",
        onRight ? "items-end" : "items-start",
      )}
      style={orbPos.style}
      data-testid="voice-orb-root"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      {/* ─── Captions and Video Bubble Column ─── */}
      <div className={cn("flex flex-col gap-2", onRight ? "items-end mr-1.5" : "items-start ml-1.5")}>
        {/* Hands-free indicator — always visible while the mic listens for the
            wake phrase, so the user knows it is on and can turn it off. */}
        {!isActive && handsFree && wakeState !== "off" && (
          <div
            role="status"
            aria-live="polite"
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-none border border-border bg-bg-elevated/90 backdrop-blur-md",
              "text-[11px] font-mono text-fg-muted pointer-events-auto",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "w-1.5 h-1.5 rounded-none",
                wakeState === "listening" ? "bg-accent animate-pulse" : "bg-fg-muted",
              )}
            />
            {wakeState === "listening"
              ? `Listening for “${wakePhrase}” — on this device`
              : wakeState === "starting"
                ? "Starting hands-free…"
                : "Hands-free is off — see the error below"}
            <button
              type="button"
              onClick={() => setHandsFree(false)}
              className="ml-1 text-fg-muted hover:text-fg underline-offset-2 hover:underline"
              aria-label="Turn hands-free off"
            >
              Off
            </button>
          </div>
        )}
        {connecting && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center gap-2 px-3 py-1.5 rounded-none border border-border bg-bg-elevated/90 backdrop-blur-md text-[11px] font-mono text-fg-muted pointer-events-auto"
          >
            <span aria-hidden className="w-1.5 h-1.5 rounded-none bg-accent animate-pulse" />
            Connecting — go ahead, I'm listening
          </div>
        )}
        {/* Thinking pulse — shown when the agent is working but hasn't
            started streaming a reply yet, so the user has visible
            progress instead of dead air. */}
        {agentState === "thinking" && (
          <div
            className={cn(
              "px-3 py-2 bg-bg-elevated/90 border border-border backdrop-blur-md",
              "rounded-2xl rounded-br-none shadow-2xl",
              "transition-all duration-200 animate-in fade-in zoom-in-95 pointer-events-auto"
            )}
          >
            <ThinkingPulse />
          </div>
        )}
        {/* Omni caption — shown WHILE the assistant is speaking. Sits on
            top of the user caption so the conversation reads top-down. */}
        {agentState === "speaking" && assistantStreaming && (
          <div
            className={cn(
              "max-w-[280px] px-4 py-2.5 bg-bg-elevated/90 border border-border backdrop-blur-md",
              "text-xs text-fg rounded-2xl shadow-2xl font-medium",
              onRight ? "text-right" : "text-left",
              "transition-all duration-200 animate-in fade-in zoom-in-95 pointer-events-auto"
            )}
          >
            <p className="text-[10px] font-mono text-accent uppercase tracking-widest mb-1">
              Omni
            </p>
            <p className="leading-relaxed">{assistantStreaming}</p>
          </div>
        )}
        {/* You caption — the user's own live transcription. */}
        {interimText && (
          <div
            className={cn(
              "max-w-[280px] px-4 py-2.5 bg-bg-elevated/90 border border-border backdrop-blur-md",
              "text-xs text-fg rounded-2xl shadow-2xl font-medium",
              onRight ? "text-right" : "text-left",
              "transition-all duration-200 animate-in fade-in zoom-in-95 pointer-events-auto"
            )}
          >
            <p className="text-[10px] font-mono text-fg-muted uppercase tracking-widest mb-1">
              You
            </p>
            <p className="leading-relaxed">{interimText}</p>
          </div>
        )}
      </div>

      {/* ─── Floating Controls Dock & Orb ─── */}
      <div className={cn("flex items-center gap-3 pointer-events-auto", !onRight && "flex-row-reverse", !onBottom && "order-first")}>
        {/* Hover-expanding Frosted Glass Controls Dock */}
        {(expanded || isActive) && (
          <div
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated/85 border border-border backdrop-blur-xl",
              "rounded-full shadow-2xl transition-all duration-300",
              "animate-in fade-in slide-in-from-right-8 duration-200"
            )}
          >
            {/* Back to the corner: only offered once the orb has been moved. */}
            {orbPos.moved && (
              <button
                type="button"
                onClick={orbPos.reset}
                className="w-8 h-8 rounded-full flex items-center justify-center border border-border text-fg-muted hover:text-fg transition-colors duration-150"
                title="Move the orb back to the corner"
                aria-label="Move the orb back to the corner"
              >
                <LocateFixed size={14} />
              </button>
            )}

            {/* Sound cues: off until asked for, so nothing beeps unprompted. */}
            <button
              type="button"
              onClick={() => feedback.setSoundOn(!feedback.soundOn)}
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center border transition-colors duration-150",
                feedback.soundOn ? "bg-accent/10 border-accent/30 text-accent hover:bg-accent/20" : "border-border text-fg-muted hover:text-fg",
              )}
              title={feedback.soundOn ? "Sound cues on: a tone when a call connects and ends. Click to turn off." : "Sound cues off. Click for a tone when a call connects and ends."}
              aria-pressed={feedback.soundOn}
              aria-label="Sound cues"
            >
              {feedback.soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>

            {/* Mic Toggle Button */}
            <button
              onClick={toggleMic}
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-200",
                micActive
                  ? "bg-accent/10 border-accent/30 text-accent hover:bg-accent/20"
                  : "bg-danger/10 border-danger/30 text-danger hover:bg-danger/20"
              )}
              title={micActive ? "Mute Microphone" : "Unmute Microphone"}
            >
              {micActive ? <Mic size={14} /> : <MicOff size={14} />}
            </button>

            {/* Hands-free — listen for the wake phrase while no call runs. */}
            {!isActive && wakeAllowed && (
              <button
                type="button"
                onClick={() => setHandsFree(!handsFree)}
                className={cn(
                  "w-8 h-8 rounded-none flex items-center justify-center border transition-colors duration-150",
                  handsFree
                    ? "bg-accent/10 border-accent/30 text-accent hover:bg-accent/20"
                    : "border-border text-fg-muted hover:text-fg",
                )}
                title={
                  handsFree
                    ? `Hands-free on: say “${wakePhrase}” to start a call. Click to turn off.`
                    : `Hands-free: start a call by saying “${wakePhrase}” (listens on this device only)`
                }
                aria-pressed={handsFree}
                aria-label="Hands-free"
              >
                {handsFree ? <Ear size={14} /> : <EarOff size={14} />}
              </button>
            )}

            {/* Voice mode — chosen before a call; fixed once it starts. */}
            {!isActive && modes.length > 1 && (
              <button
                type="button"
                onClick={() => setMode(modes[(modes.indexOf(mode) + 1) % modes.length])}
                className={cn(
                  "h-8 px-2.5 rounded-none border border-border text-[11px] font-mono uppercase tracking-wider",
                  "text-fg-muted hover:text-fg hover:border-accent/40 transition-colors duration-150",
                )}
                title={`Voice: ${MODE_LABEL[mode].hint}. Click to switch.`}
                aria-label={`Voice mode ${MODE_LABEL[mode].name}; switch mode`}
              >
                {MODE_LABEL[mode].name}
              </button>
            )}

            {/* End Call / Cancel Button */}
            {isActive && (
              <button
                onClick={cancelActiveCall}
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center border",
                  "bg-danger/20 border-danger/40 text-danger hover:bg-danger/35 transition-all duration-200"
                )}
                title="End the voice call"
              >
                <PhoneOff size={14} />
              </button>
            )}
          </div>
        )}

        {/* ─── Floating Volumetric DysonSphere Core ─── */}
        {/* Siri-style amplitude pulse: while the assistant is speaking,
            the orb's transform scale tracks ``assistantLevel`` (peak PCM
            amplitude smoothed in the speaker worklet). Range 1.05 →
            1.18 over [0, 1] gives a perceptible "breathing with the
            voice" feel without overshooting the layout. drop-shadow
            also intensifies with level so the halo "pulses" too. */}
        {(() => {
          const speaking = agentState === "speaking";
          const thinking = voiceActive && agentState === "thinking";
          const listening = voiceActive && agentState === "listening" && micActive;
          const lvl = speaking ? Math.min(1, assistantLevel) : 0;
          const baseScale = isActive ? 1.05 : 1;
          const speakingBoost = speaking ? 0.13 * lvl : 0;
          const finalScale = baseScale + speakingBoost;
          const shadowAlpha = isActive ? 0.45 + 0.45 * lvl : 0;
          // Leans toward the user while they talk: the harder the voice, the
          // further it tilts. Capped so a shout does not tip it over.
          const vol = listening ? Math.min(1, micVolume) : 0;
          const lean = `translate(${(-3 * vol).toFixed(2)}px, ${(2 * vol).toFixed(2)}px) rotate(${(-7 * vol).toFixed(2)}deg)`;
          // The button never moves: the personality (breathe, hop, lean, squish) plays
          // on the sphere inside it. A button that drifts under the pointer is harder
          // to hit — and to click from a test, which waits for it to hold still.
          const idleOrHop = speaking
            ? "om-orb-hop 0.9s ease-in-out infinite"
            : !isActive
              ? "om-orb-idle 4.5s ease-in-out infinite"
              : undefined;
          return (
            <div className="relative w-16 h-16">
              {/* Voice ripples: rings leave the orb while it answers. */}
              {speaking &&
                [0, 1].map((i) => (
                  <span
                    key={i}
                    aria-hidden
                    data-testid="orb-ripple"
                    className="absolute inset-0 rounded-full border border-accent/50 pointer-events-none"
                    style={{
                      animation: "dyson-ripple-ring 1.8s ease-out infinite",
                      animationDelay: `${i * 0.9}s`,
                    }}
                  />
                ))}
              {/* Thinking: three dots circle the sphere. */}
              {thinking &&
                [0, 1, 2].map((i) => (
                  <span
                    key={i}
                    aria-hidden
                    data-testid="orb-think-dot"
                    className="absolute left-1/2 top-1/2 -ml-[3px] -mt-[3px] w-[6px] h-[6px] rounded-full bg-accent pointer-events-none"
                    style={{
                      animation: "om-orb-orbit 2.4s linear infinite",
                      animationDelay: `${-i * 0.8}s`,
                      opacity: 0.85,
                    }}
                  />
                ))}
              {/* Done: one happy spark when an answer ends. */}
              {sparkKey > 0 &&
                SPARKS.map((d, i) => (
                  <span
                    key={`${sparkKey}-${i}`}
                    aria-hidden
                    data-testid="orb-spark"
                    className="absolute left-1/2 top-1/2 -ml-[2px] -mt-[2px] w-[4px] h-[4px] rounded-full bg-accent pointer-events-none"
                    style={
                      {
                        "--spark-x": d.x,
                        "--spark-y": d.y,
                        animation: "om-orb-spark 0.7s ease-out forwards",
                        animationDelay: `${i * 70}ms`,
                        opacity: 0,
                      } as React.CSSProperties
                    }
                  />
                ))}
              <button
                type="button"
                {...orbPos.handlers}
                onClick={() => {
                  // The click a drag ends with is not a request to start or end a call.
                  if (orbPos.consumeClick()) return;
                  squish();
                  feedback.tap();
                  toggleVoice();
                }}
                className={cn(
                  "relative w-16 h-16 rounded-full flex items-center justify-center select-none bg-transparent border-0 outline-none",
                  orbPos.dragging ? "cursor-grabbing" : "cursor-grab",
                  "shadow-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60",
                  isActive ? "hover:brightness-110" : "hover:scale-[1.03]",
                )}
                style={{
                  // The pointer drives the drag: the browser must not scroll or zoom the page instead.
                  touchAction: "none",
                  transform: `scale(${finalScale})`,
                  // Only ``transform`` is animated on the button — it is
                  // GPU-composited and does not re-raster the 3D sphere (an
                  // animated drop-shadow did, and dropped the frame rate).
                  transition: "transform 80ms linear",
                }}
                title={isActive ? "End the voice call (drag to move)" : "Talk to Omni (drag to move)"}
                aria-label={isActive ? "End voice call" : "Start voice call"}
                data-testid="voice-orb"
                aria-keyshortcuts="Shift+ArrowLeft Shift+ArrowRight Shift+ArrowUp Shift+ArrowDown"
              >
                {/* Composited outer voice halo (static blur; opacity and
                    transform animate on the compositor). */}
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: "radial-gradient(circle, var(--om-color-accent) 0%, transparent 68%)",
                      filter: "blur(10px)",
                      opacity: shadowAlpha,
                      transform: `scale(${1.15 + 0.5 * lvl})`,
                      transition: "opacity 80ms linear, transform 80ms linear",
                      zIndex: -1,
                      willChange: "opacity, transform",
                    }}
                  />
                )}
                {/* Squish on click, restarted by the key. */}
                <span
                  key={squishKey}
                  className="block w-full h-full"
                  style={squishKey > 0 ? { animation: "om-orb-squish 0.35s ease-out" } : undefined}
                >
                  {/* Lean toward the user with their voice level. */}
                  <span
                    className="block w-full h-full"
                    style={{ transform: lean, transition: "transform 120ms ease-out" }}
                  >
                    {/* Breathe while idle, hop while answering. */}
                    <span className="block w-full h-full" style={{ animation: idleOrHop }}>
                      <DysonSphere
                        mode={getDysonMode()}
                        glow={speaking ? lvl : agentState === "listening" ? micVolume : 0}
                        className="w-full h-full"
                      />
                    </span>
                  </span>
                </span>
              </button>
              {/* One word for what it is doing: the state is readable without
                  decoding the animation. */}
              {label && (
                <span
                  // The connecting bubble above is already a live region.
                  role={connecting ? undefined : "status"}
                  aria-live={connecting ? undefined : "polite"}
                  data-testid="orb-label"
                  className="absolute left-1/2 top-full mt-1.5 -translate-x-1/2 whitespace-nowrap text-[10px] font-mono uppercase tracking-widest text-fg-muted pointer-events-none"
                >
                  {label}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* ─── Floating System Error Notification ─── */}
      {error && (
        <div
          className={cn(
            "flex items-start gap-2.5 px-3.5 py-2 bg-danger/10 border border-danger/30 text-danger text-xs rounded-lg shadow-xl",
            "max-w-[340px] animate-in fade-in slide-in-from-bottom-2 duration-200 pointer-events-auto relative group pr-8"
          )}
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span className="break-all select-text leading-relaxed">{error}</span>
          <button
            onClick={handleCopyError}
            className="absolute right-1.5 top-1.5 w-5 h-5 rounded flex items-center justify-center border border-danger/25 hover:border-danger/40 bg-danger/5 hover:bg-danger/15 text-danger opacity-75 hover:opacity-100 transition-all duration-150"
            title="Copy error message to clipboard"
          >
            {copiedError ? <Check size={10} /> : <Copy size={10} />}
          </button>
        </div>
      )}
    </div>
  );
}
